/**
 * Collapse Control Component
 * Renders array items as a table with expandable row panels
 * HARD mode only: Data stored inline in object as array
 */

import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Paper,
} from '@mui/material';
import { BaseButton } from '../BaseButton';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { ControlComponentProps } from '../../../../types/ControlTypes';
import { LabelWithInfo } from './LabelWithInfo';

export interface CollapseSettings {
  maxItems?: number;
  minItems?: number;
  childs?: Array<Record<string, any>>;
  collapsible?: boolean;
  defaultExpanded?: number | null;
  summaryFields?: string[];
  /** Comma-separated field keys to show as table columns, e.g. "title, url" */
  columns?: string;
}

export const CollapseControl: React.FC<ControlComponentProps> = ({
  name,
  path,
  value,
  onChange,
  error,
  disabled,
  readonly,
  label,
  description,
  required,
  control,
  renderChild,
}) => {
  const settings = control.settings as CollapseSettings;
  const minItems = settings?.minItems || 0;
  const maxItems = settings?.maxItems;
  const childs = (settings?.childs as Array<Record<string, any>>) || [];

  // Data is always an array (HARD mode)
  const items = Array.isArray(value) ? value : [];

  const [expandedIndex, setExpandedIndex] = useState<number | null>(
    settings?.defaultExpanded ?? null
  );

  // ── Derive column definitions ──────────────────────────────────────────────
  // Key derivation: propName (new schema) > title lowercased (old schema) > label lowercased
  const toDataKey = (c: any, i: number): string => {
    if (c['x-control']?.propName) return c['x-control'].propName;
    if (c.title) return c.title.toLowerCase().replace(/\s+/g, '_');
    if (c['x-control']?.label) return c['x-control'].label.toLowerCase().replace(/\s+/g, '_');
    return `field_${i}`;
  };

  // If `columns` setting is defined, use those keys; otherwise show all childs.
  const configuredKeys: string[] = settings?.columns
    ? settings.columns.split(',').map((s) => s.trim()).filter(Boolean)
    : [];

  const columnDefs: Array<{ key: string; label: string }> = (() => {
    if (configuredKeys.length > 0) {
      return configuredKeys.map((key) => {
        const childDef = childs.find((c, i) => toDataKey(c, i) === key);
        return { key, label: childDef?.['x-control']?.label || childDef?.title || key };
      });
    }
    // Fallback: all child fields
    return childs.map((c, i) => ({
      key: toDataKey(c, i),
      label: c['x-control']?.label || c.title || `Field ${i + 1}`,
    }));
  })();

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleAddItem = () => {
    if (maxItems && items.length >= maxItems) return;
    const newItems = [...items, {}];
    onChange(newItems);
    setExpandedIndex(newItems.length - 1);
  };

  const handleRemoveItem = (index: number) => {
    if (items.length <= minItems) return;
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems);
    if (expandedIndex === index) {
      setExpandedIndex(null);
    } else if (expandedIndex !== null && expandedIndex > index) {
      setExpandedIndex(expandedIndex - 1);
    }
  };

  const handleItemChange = (index: number, newValue: any) => {
    const newItems = [...items];
    newItems[index] = newValue;
    onChange(newItems);
  };

  const toggleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  // ── Render child form for expanded panel ───────────────────────────────────
  const renderChildren = (item: any, index: number) => {
    if (!renderChild) {
      return (
        <Box sx={{ p: 2, color: '#999', border: '1px solid #ff9999' }}>
          ⚠️ renderChild function not provided
        </Box>
      );
    }
    if (!Array.isArray(childs) || childs.length === 0) {
      return (
        <Box sx={{ p: 2, color: '#999' }}>No child fields defined in schema</Box>
      );
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {childs.map((fieldDef: any, fieldIndex: number) => {
          const fieldKey = toDataKey(fieldDef, fieldIndex);
          return (
            <Box key={fieldIndex}>
              {renderChild({
                schema: fieldDef,
                value: item[fieldKey],
                onChange: (newValue: any) => {
                  handleItemChange(index, { ...item, [fieldKey]: newValue });
                },
                fieldPath: `${path}[${index}].${fieldKey}`,
              })}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{ mb: 2 }}>
      {/* Label */}
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <LabelWithInfo label={label || name} description={description} required={required} />
        <Typography variant="caption" sx={{ color: '#999' }}>
          {items.length}{maxItems ? `/${maxItems}` : ''} items
        </Typography>
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: '1px solid #E6E7EB', borderRadius: '8px', mb: 1.5, overflow: 'hidden' }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#F2F3F5' }}>
              {/* Expand toggle column */}
              <TableCell sx={{ width: 40, p: '6px 8px', borderBottom: '1px solid #E6E7EB' }} />
              {columnDefs.length > 0 ? (
                columnDefs.map((col) => (
                  <TableCell
                    key={col.key}
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: '#666',
                      p: '6px 12px',
                      borderBottom: '1px solid #E6E7EB',
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))
              ) : (
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: '#666',
                    p: '6px 12px',
                    borderBottom: '1px solid #E6E7EB',
                  }}
                >
                  Item
                </TableCell>
              )}
              {/* Actions column */}
              {!readonly && (
                <TableCell sx={{ width: 44, p: '6px 8px', borderBottom: '1px solid #E6E7EB' }} />
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columnDefs.length + (readonly ? 1 : 2)}
                  sx={{ textAlign: 'center', color: '#999', py: 3, border: 'none' }}
                >
                  No items added yet
                </TableCell>
              </TableRow>
            ) : (
              items.map((item, index) => (
                <React.Fragment key={index}>
                  {/* Data row */}
                  <TableRow
                    hover
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: expandedIndex === index ? '#f8f8f9' : 'transparent',
                      '& td': { borderBottom: expandedIndex === index ? 'none' : '1px solid #E6E7EB' },
                    }}
                    onClick={() => toggleExpand(index)}
                  >
                    {/* Expand icon */}
                    <TableCell sx={{ p: '4px 8px', width: 40 }}>
                      <IconButton size="small" sx={{ color: '#005e5d', p: 0.5 }}>
                        {expandedIndex === index ? (
                          <ExpandMoreIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <ChevronRightIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </TableCell>

                    {/* Column values */}
                    {columnDefs.length > 0 ? (
                      columnDefs.map((col) => {
                        const cellValue = item[col.key];
                        return (
                          <TableCell key={col.key} sx={{ p: '6px 12px', fontSize: '0.8125rem', color: '#333' }}>
                            {cellValue !== undefined && cellValue !== null && cellValue !== '' ? (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: '0.8125rem',
                                  maxWidth: 220,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {String(cellValue)}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: '#ccc' }}>
                                —
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })
                    ) : (
                      <TableCell sx={{ p: '6px 12px', fontSize: '0.8125rem', color: '#999' }}>
                        Item {index + 1}
                      </TableCell>
                    )}

                    {/* Delete button */}
                    {!readonly && (
                      <TableCell sx={{ p: '4px 8px', width: 44 }} onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={items.length <= minItems}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveItem(index);
                          }}
                          sx={{ p: 0.5, '&:hover': { backgroundColor: 'rgba(211,47,47,0.08)' } }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>

                  {/* Expanded panel row */}
                  <TableRow>
                    <TableCell
                      colSpan={columnDefs.length + (readonly ? 1 : 2)}
                      sx={{ p: 0, border: 'none' }}
                    >
                      <Collapse in={expandedIndex === index} timeout="auto" unmountOnExit>
                        <Box
                          sx={{
                            p: 2,
                            backgroundColor: '#f8f8f9',
                            borderTop: '1px solid #ebebeb',
                            borderBottom: '1px solid #E6E7EB',
                          }}
                        >
                          {renderChildren(item, index)}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Button */}
      {!readonly && (
        <BaseButton
          variant="outlined"
          colorVariant="primary"
          startIcon={<AddIcon />}
          onClick={handleAddItem}
          disabled={!!(maxItems && items.length >= maxItems)}
          size="small"
        >
          Add Item
        </BaseButton>
      )}

      {error && (
        <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default CollapseControl;
